import PropTypes from 'prop-types';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { debounce, get } from 'lodash';

import { MainContext } from '@ezbobdev/ui-framework';

import { useProtectedComponent } from '../hooks/useProtectedComponent/useProtectedComponent';
import { useEventEmitter } from './useEventEmitter';
import { useUtils } from './useUtils';

const defaultOptions = {
  includeCaseMapProps: [],
  onChangeDebounce: 750,
};

export const withWidget = (ComponentWrapper, options = defaultOptions) => {
  options.includeCaseMapProps = options.includeCaseMapProps || [];

  const Wrapper = React.forwardRef((props, ref) => {
    const { toCase = true, overrideConfig, ...restProps } = props;
    const { actions, view } = useContext(MainContext);
    const { configByName, getConfig } = useUtils(props);
    const { emitEvent } = useEventEmitter();

    const [originalConfig] = useState(actions.settings.getUserConfigProp(`widgetsConfig.${configByName}`));

    const newConfig =
      typeof overrideConfig === 'function'
        ? overrideConfig(actions.settings.getUserConfigProp(`widgetsConfig.${configByName}`))
        : undefined;

    const latestCase = actions.case.getLatestCase();

    const hasPermissions = !!getConfig('permissions');

    const isRequired = useMemo(() => {
      if (props.hasOwnProperty('isRequired')) {
        return props.isRequired;
      }
      return actions.caseValidation.isRequired(configByName);
    }, [props.isRequired, configByName]);

    useEffect(() => {
      actions.case.initInCase(props);
    }, [props.configBy]);

    useEffect(() => {
      if (toCase) {
        actions.case.setPropsToCaseMap(props, options.includeCaseMapProps);
      } else {
        actions.case.removeCaseMapFromProps(props, options.includeCaseMapProps);
      }

      return () => {
        if (toCase) {
          actions.case.removeCaseMapFromProps(props, options.includeCaseMapProps);
        }
      };
    }, []);

    useEffect(() => {
      const updateCase = [];
      options.includeCaseMapProps.forEach((prop) => {
        const name = props[prop];
        const value = actions.case.getLatestCase(name);
        if (value !== undefined) {
          updateCase.push({ name, value, validate: true, configBy: props.configBy });
        }
      });
      actions.case.updateInCase(updateCase);
    }, [latestCase, props.configBy]);

    // Handle getting value by props and set it on context
    useEffect(() => {
      if (props.hasOwnProperty('value') && props.value !== undefined) {
        actions.case.updateInCase(props.name, props.value, true, props.configBy);
      } else if (props.hasOwnProperty('value')) {
        actions.case.resetInCase(props.name);
      }
    }, [JSON.stringify(props.value)]);

    // Support for dynamic configuration
    useEffect(() => {
      if (newConfig) {
        return actions.settings.setUserConfigProp(`widgetsConfig.${configByName}`, newConfig);
      }

      if (originalConfig) {
        return actions.settings.setUserConfigProp(`widgetsConfig.${configByName}`, originalConfig);
      }
    }, [JSON.stringify(newConfig)]);

    // This method allow us to write a value to extra names we set on the widget at once
    const handleExtraNames = useCallback(
      (value) => {
        if (props.extraNames && props.extraNames.length) {
          const extra = props.extraNames.map((extraName) => ({
            name: extraName,
            value,
            validate: false,
            configBy: props.configBy,
          }));
          actions.case.updateInCase(extra);
        }
      },
      [props.extraNames],
    );

    const validateOnChange = getConfig('validateOnChange', true);
    const validateOnBlur = getConfig('validateOnBlur', true);

    const handleChange = useCallback(
      (e, modifiedName) => {
        const name = typeof modifiedName === 'string' ? modifiedName : props.name;
        let value = e;

        if (e && e.target) {
          const useRawValue = getConfig(`formattingOptions.useRawValue`, true);
          value = useRawValue ? get(e, 'target.rawValue', e.target.value) : e.target.value;
        }

        if (props.onChange) {
          props.onChange(value, name);
        }

        actions.case.updateInCase(name, value, validateOnChange, props.configBy);

        handleExtraNames(value);
      },
      [actions.case, props.name, props.onChange, getConfig, handleExtraNames, validateOnChange],
    );

    const handleDebouncedChange = useCallback(
      debounce((e, modifiedName) => handleChange(e, modifiedName), options.onChangeDebounce, { leading: false }),
      [handleChange],
    );

    const onChange = useCallback(
      (e, modifiedName) => {
        if (props.multiline) return handleChange(e, modifiedName);
        return handleDebouncedChange(e, modifiedName);
      },
      [props.multiline, handleChange, handleDebouncedChange],
    );

    const onBlur = useCallback(
      (e, modifiedName) => {
        let value = e;
        if (e?.type) {
          const useRawValue = getConfig(`formattingOptions.useRawValue`, true);

          value = useRawValue ? get(e, 'target.rawValue', e.target.value) : e.target.value;

          emitEvent(e.type, {
            widgetName: configByName,
            label: e.target.textContent ?? undefined,
          });
        }

        const name = modifiedName || props.name;

        if (props.onBlur) {
          props.onBlur(value, name);
        }

        if (validateOnBlur) actions.case.updateInCase(name, value, validateOnBlur, props.configBy);
        handleExtraNames(value);
      },
      [props.onBlur, validateOnBlur],
    );

    const onInput = useCallback(
      (e, modifiedName) => {
        const name = typeof modifiedName === 'string' ? modifiedName : props.name;

        queueMicrotask?.(() => {
          const target = e.target;
          if (!target) return;

          const useRawValue = getConfig('formattingOptions.useRawValue', true);
          const raw = get(target, 'rawValue');
          const next = useRawValue && raw != null ? raw : target.value;

          props.onChange?.(next, name);
          actions.case.updateInCase(name, next, validateOnChange, props.configBy);
          handleExtraNames(next);
        });
      },
      [props.name, props.onChange, getConfig, handleExtraNames, validateOnChange],
    );

    const component = useMemo(
      () => (
        <ComponentWrapper
          ref={ref}
          {...restProps}
          isRequired={isRequired}
          onChange={onChange}
          onBlur={onBlur}
          onInput={onInput}
        />
      ),
      [ComponentWrapper, props],
    );

    const { renderComponent: renderProtectedComponent } = useProtectedComponent({
      ...props,
      name: configByName,
      children: component,
    });

    if (getConfig(`hidden`)) return null;

    if (
      get(view, 'routeSettings.excludeModules') &&
      get(view, 'routeSettings.excludeModules').includes(component.type.displayName)
    ) {
      return null;
    }

    const currentStep = actions.userState.getCurrentStep() || 0;
    const stepsConfig = actions.settings.getUserConfigProp('steps');
    if (
      get(stepsConfig, `${currentStep}.excludeModules`) &&
      get(stepsConfig, `${currentStep}.excludeModules`).includes(component.type.displayName)
    ) {
      return null;
    }

    if (
      get(view, 'modal.modalSettings.excludeModules') &&
      get(view, 'modal.modalSettings.excludeModules').includes(component.type.displayName)
    ) {
      return null;
    }

    if (hasPermissions) {
      return renderProtectedComponent();
    }

    return component;
  });

  Wrapper.propTypes = {
    overrideConfig: PropTypes.func,
    configBy: PropTypes.string,
    onChange: PropTypes.func,
    onBlur: PropTypes.func,
    extraNames: PropTypes.array,
    hidden: PropTypes.bool,
    toCase: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  };

  return React.memo(Wrapper);
};
